import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Plus, X, Layers, GripVertical, Trash2 } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha, hoyCaracas } from '../utils/format';
import Modal from '../components/Modal';
import AutocompleteProducto from '../components/AutocompleteProducto';
import SimulacionCosto from './SimulacionCosto';
import EstructuraCostoGrupos from './EstructuraCostoGrupos';
import BotonAyuda from '../components/BotonAyuda';


// Cantidades a 2 decimales, sin ceros de relleno (1,00 -> "1"; 0,2700 -> "0.27").
const fmtCant = (n) => {
  const x = Number(n);
  return Number.isFinite(x) ? String(Math.round(x * 100) / 100) : (n ?? '');
};

// Bucket para agrupar la lista de la izquierda.
function bucketDe(p) {
  if (p.disponibilidad === 'contable') return 'Actividades';
  if (p.tipo === 'terminado') return 'Terminados';
  if (p.tipo === 'intermedio') return 'Intermedios';
  return 'Insumos';
}
const ORDEN_BUCKETS = ['Terminados', 'Intermedios', 'Actividades', 'Insumos'];

const FORM_LINEA_VACIO = {
  tipoLinea: 'componente', // 'componente' | 'manual'
  componente_id: null, cantidad: '', es_costo_compra: false,
  descripcion: '', manual_modo: 'fijo', costo_manual: '', costo_manual_moneda: 'USD'
};

export default function EstructuraCosto({ embebido = false } = {}) {
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;

  const [productos, setProductos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [selId, setSelId] = useState(null);
  const [tab, setTab] = useState('estructura');
  const [busqueda, setBusqueda] = useState('');
  const [modalGrupos, setModalGrupos] = useState(false);

  // --- estructura ---
  const [tasa, setTasa] = useState('BCV');
  const [lineas, setLineas] = useState([]);
  const [costoVivo, setCostoVivo] = useState(null);
  const [mermaInput, setMermaInput] = useState(0);
  const [mermaAbierta, setMermaAbierta] = useState(false);
  const [rendimientoInput, setRendimientoInput] = useState(1);
  const [rendimientoAbierta, setRendimientoAbierta] = useState(false);
  const [qtyEdits, setQtyEdits] = useState({});
  const [costoEdits, setCostoEdits] = useState({});
  const [dragLinea, setDragLinea] = useState(null);
  const [dragOverGrupo, setDragOverGrupo] = useState(undefined);
  const [addGrupo, setAddGrupo] = useState(undefined); // undefined = cerrado; null o id = abierto en ese grupo
  const [formLinea, setFormLinea] = useState(FORM_LINEA_VACIO);
  const [errorLinea, setErrorLinea] = useState('');
  const [guardandoLinea, setGuardandoLinea] = useState(false);
  const [recargando, setRecargando] = useState(false);

  // --- precios ---
  const [precios, setPrecios] = useState([]);
  const [rentab, setRentab] = useState({});
  const [formPrecio, setFormPrecio] = useState({ tipo_precio: 'detal', tipo_tasa: 'USD', precio: '', vigente_desde: hoyCaracas() });
  const [errorPrecio, setErrorPrecio] = useState('');
  const [precioAbierto, setPrecioAbierto] = useState(false);

  const sel = productos.find((p) => p.id === selId) || null;

  async function cargarBase() {
    const [prod, grp] = await Promise.all([api.get('/productos'), api.get('/estructura-costo-grupo')]);
    setProductos(prod.data);
    setGrupos(grp.data);
  }

  const lsKey = empresaActiva ? `estructura-costo:${empresaActiva}` : null;
  const restaurarRef = useRef(null); // { selId, tab } leído de localStorage al abrir

  useEffect(() => {
    if (!empresaActiva) return;
    try {
      const g = JSON.parse(localStorage.getItem(lsKey) || 'null');
      restaurarRef.current = g && g.selId ? g : null;
    } catch { restaurarRef.current = null; }
    cargarBase();
    setSelId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  // Recordar producto + pestaña para sobrevivir un refresh.
  useEffect(() => {
    if (!lsKey || !selId) return;
    try { localStorage.setItem(lsKey, JSON.stringify({ selId, tab })); } catch { /* ignore */ }
  }, [lsKey, selId, tab]);

  // ---------- estructura ----------
  async function cargarReceta(id, t) {
    setRecargando(true);
    try {
      const [ls, vivo] = await Promise.all([
        api.get('/producto-receta', { params: { producto_id: id } }),
        api.get('/producto-receta/costo-vivo', { params: { producto_id: id, tasa: t || tasa } })
      ]);
      setLineas(ls.data);
      setCostoVivo(vivo.data);
      setQtyEdits({});
      setCostoEdits({});
    } finally {
      setRecargando(false);
    }
  }

  function elegir(id) {
    setSelId(id);
    setErrorLinea(''); setErrorPrecio('');
    setAddGrupo(undefined);
    setFormLinea(FORM_LINEA_VACIO);
    setTasa('BCV');
    setLineas([]); setCostoVivo('cargando');
    setMermaInput(Number(productos.find((x) => x.id === id)?.merma_pct) || 0);
    setMermaAbierta(false);
    setRendimientoInput(Number(productos.find((x) => x.id === id)?.rendimiento) || 1);
    setRendimientoAbierta(false);
    cargarReceta(id, 'BCV').catch(() => setCostoVivo(null));
  }

  async function cambiarTasa(t) {
    setTasa(t);
    setCostoVivo('cargando');
    try { await cargarReceta(selId, t); } catch { setCostoVivo(null); }
  }

  async function guardarCantidad(linea) {
    const nuevo = qtyEdits[linea.id];
    if (nuevo === undefined || nuevo === '' || Number(nuevo) === Number(linea.cantidad)) return;
    try {
      await api.put(`/producto-receta/${linea.id}`, { cantidad: nuevo });
      await cargarReceta(selId);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar la cantidad');
      setQtyEdits((q) => { const n = { ...q }; delete n[linea.id]; return n; });
    }
  }

  async function guardarCostoManual(linea) {
    const nuevo = costoEdits[linea.id];
    if (nuevo === undefined || nuevo === '' || Number(nuevo) === Number(linea.costo_manual)) return;
    try {
      await api.put(`/producto-receta/${linea.id}`, { costo_manual: nuevo, costo_manual_moneda: linea.costo_manual_moneda });
      await cargarReceta(selId);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar el costo');
      setCostoEdits((q) => { const n = { ...q }; delete n[linea.id]; return n; });
    }
  }

  async function cambiarMonedaManual(linea, moneda) {
    if (moneda === linea.costo_manual_moneda) return;
    try {
      await api.put(`/producto-receta/${linea.id}`, { costo_manual: linea.costo_manual, costo_manual_moneda: moneda });
      await cargarReceta(selId);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo cambiar la moneda');
    }
  }

  async function cambiarGrupoLinea(linea, grupoId) {
    try {
      await api.put(`/producto-receta/${linea.id}`, { grupo_id: grupoId || null });
      await cargarReceta(selId);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo cambiar el grupo');
    }
  }

  async function eliminarLinea(linea) {
    if (!window.confirm('¿Quitar esta línea de la estructura?')) return;
    try {
      await api.delete(`/producto-receta/${linea.id}`);
      await cargarReceta(selId);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar');
    }
  }

  async function agregarLinea(e) {
    e.preventDefault();
    if (guardandoLinea) return;
    setErrorLinea('');
    const f = formLinea;
    let payload;

    if (f.tipoLinea === 'manual') {
      if (!f.descripcion.trim()) { setErrorLinea('Escribí el concepto.'); return; }
      if (!f.cantidad) { setErrorLinea(f.manual_modo === 'porcentaje' ? 'Indicá el porcentaje.' : 'Indicá la cantidad.'); return; }
      if (f.manual_modo === 'fijo' && (f.costo_manual === '' || Number(f.costo_manual) < 0)) { setErrorLinea('Indicá el costo.'); return; }
      payload = {
        producto_id: selId, es_manual: true,
        descripcion: f.descripcion.trim(),
        manual_modo: f.manual_modo,
        costo_manual: f.manual_modo === 'fijo' ? f.costo_manual : null,
        costo_manual_moneda: f.manual_modo === 'fijo' ? f.costo_manual_moneda : null,
        cantidad: f.cantidad,
        grupo_id: addGrupo || null
      };
    } else {
      const esCC = !!f.es_costo_compra;
      if (!esCC && !f.componente_id) { setErrorLinea('Elegí un componente de la lista.'); return; }
      if (!f.cantidad) { setErrorLinea('Indicá la cantidad.'); return; }
      payload = {
        producto_id: selId, es_costo_compra: esCC,
        componente_id: esCC ? null : f.componente_id,
        cantidad: f.cantidad, grupo_id: addGrupo || null
      };
    }

    setGuardandoLinea(true);
    try {
      await api.post('/producto-receta', payload);
      setFormLinea(FORM_LINEA_VACIO);
      setAddGrupo(undefined);
      await cargarReceta(selId);
    } catch (err) {
      setErrorLinea(err.response?.data?.error || 'No se pudo agregar la línea');
    } finally {
      setGuardandoLinea(false);
    }
  }

  async function guardarMerma() {
    try {
      const { data } = await api.put(`/productos/${selId}/merma`, { merma_pct: mermaInput });
      setProductos((prev) => prev.map((x) => (x.id === selId ? { ...x, merma_pct: data.merma_pct } : x)));
      await cargarReceta(selId);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar la merma');
    }
  }

  async function guardarRendimiento() {
    try {
      const { data } = await api.put(`/productos/${selId}/rendimiento`, { rendimiento: rendimientoInput });
      setProductos((prev) => prev.map((x) => (x.id === selId ? { ...x, rendimiento: data.rendimiento } : x)));
      setRendimientoInput(data.rendimiento);
      await cargarReceta(selId);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar el rendimiento');
    }
  }

  // ---------- precios ----------
  async function cargarPrecios(id) {
    const { data } = await api.get('/producto-precio', { params: { producto_id: id, vigentes: 1 } });
    setPrecios(data);
    // Una combinación tipo/tasa = una llamada de rentabilidad. Cada una
    // tolera su propio error para que un fallo no borre las demás.
    const combos = [...new Set(data.map((p) => `${p.tipo_precio}|${p.tipo_tasa}`))];
    const res = await Promise.all(combos.map(async (c) => {
      const [tp, tt] = c.split('|');
      try {
        const { data: r } = await api.get('/producto-precio/rentabilidad', { params: { producto_id: id, tipo_precio: tp, tipo_tasa: tt } });
        return [c, r];
      } catch {
        return [c, null];
      }
    }));
    setRentab(Object.fromEntries(res));
  }

  useEffect(() => {
    if (tab === 'precios' && selId) {
      setPrecios([]); setRentab({});
      setErrorPrecio(''); setPrecioAbierto(false);
      setFormPrecio({ tipo_precio: 'detal', tipo_tasa: 'USD', precio: '', vigente_desde: hoyCaracas() });
      cargarPrecios(selId).catch(() => { setPrecios([]); setErrorPrecio('No se pudieron cargar los precios.'); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, selId]);

  async function agregarPrecio(e) {
    e.preventDefault();
    setErrorPrecio('');
    if (!formPrecio.precio) return;
    try {
      await api.post('/producto-precio', { producto_id: selId, ...formPrecio });
      setFormPrecio({ tipo_precio: 'detal', tipo_tasa: 'USD', precio: '', vigente_desde: hoyCaracas() });
      setPrecioAbierto(false);
      await cargarPrecios(selId);
    } catch (err) {
      setErrorPrecio(err.response?.data?.error || 'No se pudo registrar el precio');
    }
  }

  async function eliminarPrecio(precioId) {
    try {
      await api.delete(`/producto-precio/${precioId}`);
      await cargarPrecios(selId);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo quitar el precio');
    }
  }

  // ---------- lista izquierda ----------
  const listaAgrupada = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const filtrados = productos.filter((p) => !q || p.nombre.toLowerCase().includes(q));
    const map = new Map();
    filtrados.forEach((p) => {
      const b = bucketDe(p);
      if (!map.has(b)) map.set(b, []);
      map.get(b).push(p);
    });
    return ORDEN_BUCKETS.filter((b) => map.has(b)).map((b) => ({
      bucket: b,
      items: map.get(b).sort((a, c) => a.nombre.localeCompare(c.nombre, 'es'))
    }));
  }, [productos, busqueda]);

  // Al abrir: restaurar el producto + pestaña del último uso (localStorage);
  // si no hay, seleccionar el primero de la lista.
  useEffect(() => {
    if (selId || !productos.length) return;
    const guardado = restaurarRef.current;
    if (guardado && productos.some((p) => p.id === guardado.selId)) {
      restaurarRef.current = null;
      elegir(guardado.selId);
      if (guardado.tab) setTab(guardado.tab);
      return;
    }
    restaurarRef.current = null;
    const primero = listaAgrupada[0]?.items[0]?.id;
    if (primero) elegir(primero);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productos, listaAgrupada, selId]);

  // Lista estable para el autocomplete de "agregar línea" (excluye el propio
  // producto). Sin useMemo, cada render pasa un array nuevo y el combobox
  // borra lo que se está tipeando.
  const productosParaLinea = useMemo(
    () => productos.filter((x) => x.id !== selId),
    [productos, selId]
  );

  // `detalle` del costo-vivo, indexado por id de la línea de receta
  // (linea_id === producto_receta.id === l.id). Antes se indexaba por
  // componente_id, y las líneas sin componente (costo de compra + manuales)
  // colisionaban en la misma clave.
  const detalleMap = useMemo(() => {
    const m = new Map();
    (costoVivo?.detalle || []).forEach((d) => { if (d.linea_id != null) m.set(d.linea_id, d); });
    return m;
  }, [costoVivo]);

  // Todos los grupos configurados se muestran siempre (aunque estén vacíos)
  // para poder agregarles una línea. "Sin grupo" sólo aparece si tiene líneas
  // o es el destino de un alta en curso.
  const gruposConLineas = useMemo(() => {
    const orden = [
      ...[...grupos].sort((a, b) => a.orden - b.orden).map((g) => ({ id: g.id, nombre: g.nombre })),
      { id: null, nombre: 'Sin grupo' }
    ];
    return orden
      .map((g) => ({ ...g, lineas: lineas.filter((l) => (l.grupo_id ?? null) === g.id) }))
      .filter((g) => g.id !== null || g.lineas.length > 0 || addGrupo === null)
      .map((g) => {
        const subs = g.lineas.map((l) => detalleMap.get(l.id)?.subtotal);
        const incompleto = subs.some((s) => s === null || s === undefined);
        const subtotal = incompleto ? null : subs.reduce((a, b) => a + Number(b || 0), 0);
        return { ...g, subtotal };
      });
  }, [grupos, lineas, detalleMap, addGrupo]);

  return (
    <div className="space-y-4">
      {!embebido && (
        <h1 className="text-2xl font-bold text-slate-800">
          Estructura de costo
          {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
          <BotonAyuda />
        </h1>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5 items-start">

        {/* ---- lista ---- */}
        <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
          <div className="p-3">
            <div className="flex items-center gap-2 border rounded-lg px-3 py-2">
              <Search size={14} className="text-slate-400" />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar producto…"
                className="text-sm flex-1 outline-none"
              />
            </div>
          </div>
          <div className="pb-2 max-h-[70vh] overflow-y-auto">
            {listaAgrupada.map((sec) => (
              <div key={sec.bucket}>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 px-4 pt-3 pb-1">{sec.bucket}</div>
                {sec.items.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => elegir(p.id)}
                    className={`block w-full text-left px-4 py-2 text-sm ${p.id === selId ? 'bg-primary-50 border-l-[3px] border-primary-600 font-semibold text-slate-800' : 'text-slate-600 hover:bg-slate-50'}`}
                  >
                    {p.nombre}
                  </button>
                ))}
              </div>
            ))}
            {listaAgrupada.length === 0 && <p className="px-4 py-3 text-sm text-slate-400">Sin resultados.</p>}
          </div>
        </div>

        {/* ---- panel ---- */}
        {!sel ? (
          <div className="bg-white border rounded-xl shadow-sm p-10 text-center text-slate-400 text-sm">
            Elegí un producto de la lista para ver su estructura de costo.
          </div>
        ) : (
          <div>
            {/* tabs */}
            <div className="flex items-end justify-between border-b">
              <div className="flex gap-1">
                {[['estructura', 'Estructura'], ['simulacion', 'Simulación'], ['precios', 'Precios y rentabilidad']].map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setTab(k)}
                    className={`px-3.5 py-2.5 text-sm ${tab === k ? 'font-semibold text-primary-600 border-b-2 border-primary-600' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {tab === 'estructura' && (
                <div className="flex items-center gap-2 pb-1.5">
                  <label className="text-xs text-slate-500">Tasa</label>
                  <select
                    value={tasa}
                    onChange={(e) => cambiarTasa(e.target.value)}
                    className="border rounded-md px-2 py-1 text-xs"
                  >
                    <option value="BCV">BCV</option>
                    <option value="USD">USD</option>
                  </select>
                  <button onClick={() => setModalGrupos(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border rounded-lg text-xs text-slate-600 hover:bg-slate-50">
                    <Layers size={13} /> Administrar grupos
                  </button>
                </div>
              )}
            </div>

            {/* ---------- ESTRUCTURA ---------- */}
            {tab === 'estructura' && (
              <div className="bg-white border border-t-0 rounded-b-xl shadow-sm">
                <div className="px-5 py-3 border-b flex items-center justify-between gap-4">
                  <span className="text-sm text-slate-500">
                    <span className="font-semibold text-slate-700">{sel.nombre}</span> · se mide en {sel.unidad_medida}
                  </span>
                  {recargando && <span className="text-xs text-primary-600">actualizando…</span>}
                </div>

                {costoVivo && costoVivo !== 'cargando' && (
                  <div className="px-5 py-3 border-b bg-slate-50 grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Costo en vivo ({tasa}) por {sel.unidad_medida}</div>
                      <div className={`text-xl font-bold tabular-nums ${costoVivo.costo_unitario != null ? 'text-primary-600' : 'text-slate-400'}`}>
                        {costoVivo.costo_unitario != null ? `$ ${formatMonto(costoVivo.costo_unitario)}` : 'incompleto'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                        Subtotal{Number(costoVivo.merma_pct) > 0 ? ` · merma ${formatMonto(costoVivo.merma_pct)} %` : ''}
                      </div>
                      <div className="text-xl font-bold tabular-nums text-slate-700">
                        {costoVivo.costo_sin_merma != null ? `$ ${formatMonto(costoVivo.costo_sin_merma)}` : '—'}
                      </div>
                    </div>
                  </div>
                )}

                {costoVivo === 'cargando' ? (
                  <p className="p-6 text-sm text-slate-400">Cargando…</p>
                ) : (
                  <>
                    {lineas.length === 0 && (
                      <p className="px-5 py-3 text-sm text-slate-500 bg-amber-50 border-b border-amber-100">
                        Este producto todavía no tiene estructura de costo. Agregá líneas a los grupos de abajo; mientras tanto su costo sale de la carga manual o de facturas (ícono $ en Productos).
                      </p>
                    )}
                    <div className="grid grid-cols-[24px_1fr_130px_120px_110px_36px] gap-2 px-5 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 border-b">
                      <span></span><span>Componente</span><span className="text-right">Cantidad</span><span className="text-right">Costo unit.</span><span className="text-right">Importe</span><span></span>
                    </div>

                    {gruposConLineas.map((g) => {
                      const dropActivo = dragLinea && dragOverGrupo === g.id && (dragLinea.grupo_id ?? null) !== g.id;
                      return (
                      <div
                        key={g.id ?? 'sin'}
                        onDragOver={(e) => { if (dragLinea) { e.preventDefault(); setDragOverGrupo(g.id); } }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (dragLinea && (dragLinea.grupo_id ?? null) !== g.id) cambiarGrupoLinea(dragLinea, g.id);
                          setDragLinea(null); setDragOverGrupo(undefined);
                        }}
                        className={dropActivo ? 'ring-2 ring-inset ring-primary-500 bg-primary-50/40' : ''}
                      >
                        <div className="flex items-center justify-between px-5 py-2 bg-slate-100 border-b">
                          <span className="text-[13px] font-bold text-slate-700">{g.nombre}</span>
                          <span className="text-[13px] font-bold text-slate-700 tabular-nums">
                            {g.subtotal === null ? '—' : `$ ${formatMonto(g.subtotal)}`}
                          </span>
                        </div>
                        {g.lineas.map((l) => {
                          const d = detalleMap.get(l.id);
                          const esPorc = !!d?.es_porcentaje;
                          const esAct = l.componente_disponibilidad === 'contable';
                          const esManual = !!Number(l.es_manual);
                          const manualFijo = esManual && l.manual_modo === 'fijo';
                          const tieneReceta = d?.detalle && d.detalle.length > 0;
                          return (
                            <div key={l.id} className={`grid grid-cols-[24px_1fr_130px_120px_110px_36px] gap-2 px-5 py-2 text-sm items-center border-b border-slate-100 ${dragLinea?.id === l.id ? 'opacity-40' : ''}`}>
                              <span
                                draggable
                                onDragStart={() => setDragLinea(l)}
                                onDragEnd={() => { setDragLinea(null); setDragOverGrupo(undefined); }}
                                className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 justify-self-center"
                                title="Arrastrar a otro grupo"
                              >
                                <GripVertical size={14} />
                              </span>
                              <span className="inline-flex items-center gap-2">
                                {l.componente_nombre}
                                {tieneReceta && <span className="text-[10px] font-semibold text-primary-600 bg-primary-50 rounded-full px-2 py-0.5">receta</span>}
                                {esAct && <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">{esPorc ? '% s/ insumos' : 'actividad'}</span>}
                                {esManual && <span className="text-[10px] font-semibold text-slate-600 bg-slate-200 rounded-full px-2 py-0.5">{esPorc ? 'manual · % s/ insumos' : 'manual'}</span>}
                              </span>
                              <span className="justify-self-end inline-flex items-center gap-1">
                                <input
                                  type="number" step="0.01"
                                  value={qtyEdits[l.id] ?? fmtCant(l.cantidad)}
                                  onChange={(e) => setQtyEdits((q) => ({ ...q, [l.id]: e.target.value }))}
                                  onBlur={() => guardarCantidad(l)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                  className="num border border-slate-300 rounded-md px-2 py-1 w-[74px] text-right text-sm"
                                />
                                <span className="text-xs text-slate-400 w-4">{esPorc ? '%' : ''}</span>
                              </span>
                              <span className="text-right tabular-nums text-slate-500">
                                {manualFijo ? (
                                  <span className="inline-flex items-center gap-1">
                                    <input
                                      type="number" step="0.01"
                                      value={costoEdits[l.id] ?? fmtCant(l.costo_manual)}
                                      onChange={(e) => setCostoEdits((q) => ({ ...q, [l.id]: e.target.value }))}
                                      onBlur={() => guardarCostoManual(l)}
                                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                      className="num border border-slate-300 rounded-md px-2 py-1 w-[64px] text-right text-sm"
                                    />
                                    <select
                                      value={l.costo_manual_moneda === 'BS' ? 'BS' : 'USD'}
                                      onChange={(e) => cambiarMonedaManual(l, e.target.value)}
                                      className="border border-slate-300 rounded px-0.5 py-1 text-[10px] text-slate-500"
                                    >
                                      <option value="USD">USD</option>
                                      <option value="BS">BS</option>
                                    </select>
                                  </span>
                                ) : d && d.costo_unitario_componente != null ? `$ ${formatMonto(d.costo_unitario_componente)}` : (esPorc ? '—' : 'sin costo')}
                              </span>
                              <span className="text-right tabular-nums font-semibold">
                                {d && d.subtotal != null ? `$ ${formatMonto(d.subtotal)}` : '—'}
                              </span>
                              <button onClick={() => eliminarLinea(l)} className="text-slate-400 hover:text-red-600 justify-self-center">
                                <X size={14} />
                              </button>
                            </div>
                          );
                        })}
                        {g.lineas.length > 0 && (
                          <p className="px-5 pt-1.5 text-[11px] text-slate-300">
                            Arrastrá <GripVertical size={10} className="inline -mt-0.5" /> para mover una línea a otro grupo.
                          </p>
                        )}
                        {addGrupo === g.id ? (
                          <LineaForm
                            grupoNombre={g.nombre}
                            productos={productosParaLinea}
                            lineas={lineas}
                            form={formLinea} setForm={setFormLinea}
                            onSubmit={agregarLinea} onCancel={() => { setAddGrupo(undefined); setFormLinea(FORM_LINEA_VACIO); }}
                            error={errorLinea} guardando={guardandoLinea}
                          />
                        ) : (
                          <button onClick={() => { setAddGrupo(g.id); setFormLinea(FORM_LINEA_VACIO); setErrorLinea(''); }}
                            className="m-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-dashed border-slate-300 rounded-lg text-xs text-slate-500 hover:bg-slate-50">
                            <Plus size={13} /> Agregar línea a “{g.nombre}”
                          </button>
                        )}
                      </div>
                      );
                    })}

                    {/* merma + total */}
                    <div className="px-5 py-4 bg-slate-50 border-t space-y-3">
                      {(mermaAbierta || Number(mermaInput) > 0) ? (
                        <div className="flex items-center gap-3">
                          <span className="text-[11px] uppercase tracking-wide text-slate-400 font-medium w-24">Merma</span>
                          <input
                            type="range" min="0" max="10" step="0.5"
                            value={mermaInput}
                            onChange={(e) => setMermaInput(Number(e.target.value))}
                            onPointerUp={guardarMerma} onKeyUp={guardarMerma}
                            className="flex-1 max-w-xs accent-primary-600"
                            aria-label="Porcentaje de merma"
                          />
                          <span className="text-sm text-slate-600 tabular-nums w-14 text-right">{formatMonto(mermaInput)} %</span>
                          {Number(mermaInput) === 0 && (
                            <button onClick={() => setMermaAbierta(false)} className="text-xs text-slate-400 hover:underline">quitar</button>
                          )}
                        </div>
                      ) : (
                        <button onClick={() => setMermaAbierta(true)}
                          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700">
                          <Plus size={13} /> Agregar merma
                        </button>
                      )}

                      {(rendimientoAbierta || Number(rendimientoInput) > 1) ? (
                        <div className="flex items-center gap-3">
                          <span className="text-[11px] uppercase tracking-wide text-slate-400 font-medium w-24">Rendimiento</span>
                          <input
                            type="number" min="0.01" step="0.01"
                            value={rendimientoInput}
                            onChange={(e) => setRendimientoInput(e.target.value)}
                            onBlur={guardarRendimiento}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                            className="num border border-slate-300 rounded-md px-2 py-1 w-20 text-right text-sm"
                            aria-label="Unidades que rinde esta receta"
                          />
                          <span className="text-xs text-slate-500">{sel.unidad_medida} que produce esta receta — el costo se reparte entre esas unidades</span>
                          {Number(rendimientoInput) === 1 && (
                            <button onClick={() => setRendimientoAbierta(false)} className="text-xs text-slate-400 hover:underline">quitar</button>
                          )}
                        </div>
                      ) : (
                        <button onClick={() => setRendimientoAbierta(true)}
                          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700">
                          <Plus size={13} /> Agregar rendimiento (receta que produce varias unidades)
                        </button>
                      )}

                      {costoVivo?.costo_unitario != null ? (
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between text-slate-500"><span>Subtotal (sin merma)</span><span className="tabular-nums">$ {formatMonto(costoVivo.costo_sin_merma)}</span></div>
                          {Number(costoVivo.merma_pct) > 0 && (
                            <div className="flex justify-between text-slate-500"><span>Merma {formatMonto(costoVivo.merma_pct)} %</span><span className="tabular-nums">$ {formatMonto(costoVivo.merma_monto)}</span></div>
                          )}
                          {Number(costoVivo.rendimiento) > 1 && (
                            <>
                              <div className="flex justify-between text-slate-500"><span>Costo total de la receta</span><span className="tabular-nums">$ {formatMonto(costoVivo.costo_lote)}</span></div>
                              <div className="flex justify-between text-slate-500"><span>÷ rendimiento ({formatMonto(costoVivo.rendimiento)} {sel.unidad_medida})</span><span></span></div>
                            </>
                          )}
                          <div className="flex justify-between items-baseline pt-2 border-t">
                            <span className="font-bold text-slate-800">Costo en vivo por {sel.unidad_medida} (a {tasa})</span>
                            <span className="text-lg font-bold text-primary-600 tabular-nums">$ {formatMonto(costoVivo.costo_unitario)}</span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-600">Costo en vivo incompleto — falta costo registrado en algún componente.</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ---------- SIMULACIÓN ---------- */}
            {tab === 'simulacion' && (
              <div className="bg-white border border-t-0 rounded-b-xl shadow-sm p-5">
                <SimulacionCosto embebido />
              </div>
            )}

            {/* ---------- PRECIOS ---------- */}
            {tab === 'precios' && (
              <div className="bg-white border border-t-0 rounded-b-xl shadow-sm p-5 space-y-4">
                {precios.length > 0 ? (
                  <>
                    <table className="w-full text-sm">
                      <thead className="text-slate-400 text-left text-[11px] font-semibold uppercase tracking-wide">
                        <tr>
                          <th className="py-1 pr-2">Tipo</th>
                          <th className="py-1 pr-2 w-14">Tasa</th>
                          <th className="py-1 pr-2 w-28">Vigente desde</th>
                          <th className="py-1 px-2 w-28 text-right">Precio</th>
                          <th className="py-1 px-2 w-28 text-right">Costo actual</th>
                          <th className="py-1 px-2 w-28 text-right">Margen real</th>
                          <th className="py-1 w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {precios.map((pr) => {
                          const r = rentab[`${pr.tipo_precio}|${pr.tipo_tasa}`];
                          return (
                            <tr key={pr.id} className="border-t border-slate-200 hover:bg-slate-50">
                              <td className="py-1.5 pr-2 capitalize">{pr.tipo_precio}</td>
                              <td className="py-1.5 pr-2">{pr.tipo_tasa}</td>
                              <td className="py-1.5 pr-2">{formatFecha(pr.vigente_desde)}</td>
                              <td className="py-1.5 px-2 w-28 text-right tabular-nums font-medium">$ {formatMonto(pr.precio)}</td>
                              <td className="py-1.5 px-2 w-28 text-right tabular-nums text-slate-500">{r?.costo_unitario != null ? `$ ${formatMonto(r.costo_unitario)}` : '—'}</td>
                              <td className={`py-1.5 px-2 w-28 text-right tabular-nums ${r?.margen_real == null ? 'text-slate-400' : r.margen_real >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {r?.margen_real != null ? `${formatMonto(r.margen_real)} %` : '—'}
                              </td>
                              <td className="py-1.5">
                                <button onClick={() => eliminarPrecio(pr.id)} title="Eliminar" className="text-slate-400 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {precios.some((pr) => rentab[`${pr.tipo_precio}|${pr.tipo_tasa}`] && rentab[`${pr.tipo_precio}|${pr.tipo_tasa}`].costo_unitario == null) && (
                      <p className="text-xs text-amber-600">El costo en vivo está incompleto (falta costo en algún componente), por eso no se puede calcular costo ni margen.</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-slate-400">Este producto no tiene precios cargados.</p>
                )}

                {precioAbierto ? (
                  <form onSubmit={agregarPrecio} className="flex flex-wrap items-end gap-2 border border-dashed rounded-lg bg-slate-50 p-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Tipo</label>
                      <select value={formPrecio.tipo_precio} onChange={(e) => setFormPrecio({ ...formPrecio, tipo_precio: e.target.value })} className="border rounded-md px-2 py-1.5 text-sm">
                        <option value="detal">Detal</option><option value="mayor">Mayor</option><option value="fabrica">Fábrica</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Tasa</label>
                      <select value={formPrecio.tipo_tasa} onChange={(e) => setFormPrecio({ ...formPrecio, tipo_tasa: e.target.value })} className="border rounded-md px-2 py-1.5 text-sm">
                        <option value="USD">USD</option><option value="BCV">BCV</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Precio</label>
                      <input type="number" step="0.0001" required value={formPrecio.precio}
                        onChange={(e) => setFormPrecio({ ...formPrecio, precio: e.target.value })}
                        className="border rounded-md px-2 py-1.5 text-sm w-24" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Vigente desde</label>
                      <input type="date" value={formPrecio.vigente_desde}
                        onChange={(e) => setFormPrecio({ ...formPrecio, vigente_desde: e.target.value })}
                        className="border rounded-md px-2 py-1.5 text-sm" />
                    </div>
                    <button type="submit" className="bg-primary-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-primary-700">Agregar</button>
                    <button type="button" onClick={() => { setPrecioAbierto(false); setErrorPrecio(''); }} title="Cancelar" className="p-2 text-slate-400 hover:text-red-600 transition-colors">
                      <X size={16} />
                    </button>
                    {errorPrecio && <p className="text-red-600 text-xs w-full">{errorPrecio}</p>}
                  </form>
                ) : (
                  <button
                    onClick={() => { setPrecioAbierto(true); setErrorPrecio(''); }}
                    title="Agregar precio"
                    className="text-slate-400 hover:text-primary-600 transition-colors"
                  >
                    <Plus size={16} />
                  </button>
                )}
                {!precioAbierto && errorPrecio && <p className="text-red-600 text-xs">{errorPrecio}</p>}
              </div>
            )}
          </div>
        )}
      </div>

      {modalGrupos && (
        <Modal title="Grupos de estructura de costo" onClose={() => { setModalGrupos(false); cargarBase(); }} maxWidth="max-w-lg">
          <EstructuraCostoGrupos embebido onCambio={(data) => setGrupos(data)} />
        </Modal>
      )}
    </div>
  );
}

// Formulario compacto de alta de línea dentro de un grupo.
function LineaForm({ grupoNombre, productos, lineas, form, setForm, onSubmit, onCancel, error, guardando }) {
  const yaTieneCostoCompra = lineas.some((l) => l.es_costo_compra);
  const manual = form.tipoLinea === 'manual';
  const manualPorc = manual && form.manual_modo === 'porcentaje';
  return (
    <form onSubmit={onSubmit} className="m-3 p-3 border border-dashed rounded-lg bg-slate-50 space-y-2">
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500">Agregar a “{grupoNombre}”:</span>
        <div className="flex border rounded-md overflow-hidden text-xs">
          {[['componente', 'Componente'], ['manual', 'Manual']].map(([k, lbl]) => (
            <button key={k} type="button"
              onClick={() => setForm({ ...FORM_LINEA_VACIO, tipoLinea: k })}
              className={`px-3 py-1 ${form.tipoLinea === k ? 'bg-primary-600 text-white font-semibold' : 'bg-white text-slate-500'}`}>{lbl}</button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        {!manual ? (
          <div className="w-56">
            {form.es_costo_compra ? (
              <div className="border rounded-md px-3 py-2 text-sm bg-white text-slate-600">Costo de compra de este producto</div>
            ) : (
              <AutocompleteProducto
                productos={productos}
                value={form.componente_id}
                onChange={(id) => setForm({ ...form, componente_id: id })}
                placeholder="Buscar insumo, intermedio o actividad…"
              />
            )}
            {!yaTieneCostoCompra && (
              <label className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                <input type="checkbox" checked={form.es_costo_compra}
                  onChange={(e) => setForm({ ...form, es_costo_compra: e.target.checked, componente_id: null, cantidad: e.target.checked && !form.cantidad ? '1' : form.cantidad })} />
                Usar el costo de compra de este producto
              </label>
            )}
          </div>
        ) : (
          <>
            <div className="w-56">
              <label className="block text-xs text-slate-500 mb-1">Concepto</label>
              <input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                placeholder="ej. Carga fabril" className="border rounded-md px-2 py-1.5 text-sm w-full" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Modo</label>
              <select value={form.manual_modo} onChange={(e) => setForm({ ...form, manual_modo: e.target.value })}
                className="border rounded-md px-2 py-1.5 text-sm">
                <option value="fijo">Fijo ($)</option>
                <option value="porcentaje">% sobre insumos</option>
              </select>
            </div>
            {!manualPorc && (
              <>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Costo unitario</label>
                  <input type="number" step="0.01" value={form.costo_manual}
                    onChange={(e) => setForm({ ...form, costo_manual: e.target.value })}
                    className="border rounded-md px-2 py-1.5 text-sm w-24" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Moneda</label>
                  <select value={form.costo_manual_moneda} onChange={(e) => setForm({ ...form, costo_manual_moneda: e.target.value })}
                    className="border rounded-md px-2 py-1.5 text-sm">
                    <option value="USD">USD</option>
                    <option value="BS">BS</option>
                  </select>
                </div>
              </>
            )}
          </>
        )}
        <div>
          <label className="block text-xs text-slate-500 mb-1">{manualPorc ? '%' : 'Cantidad'}</label>
          <input type="number" step="0.01" required value={form.cantidad}
            onChange={(e) => setForm({ ...form, cantidad: e.target.value })}
            className="border rounded-md px-2 py-1.5 text-sm w-24" />
        </div>
        <button type="submit" disabled={guardando}
          className="bg-primary-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-primary-700 disabled:opacity-60 disabled:cursor-wait">
          {guardando ? 'Guardando…' : 'Agregar'}
        </button>
        <button type="button" onClick={onCancel} disabled={guardando} className="text-slate-500 text-sm hover:underline disabled:opacity-60">Cancelar</button>
      </div>
      {error && <p className="text-red-600 text-xs">{error}</p>}
    </form>
  );
}
